import React, { useState, useEffect } from "react";
import { Briefcase, FileText, Users, Mailbox, MessageCircle, Building2, Newspaper, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import api from "@/lib/api";

const CARDS = [
  { k: "published_jobs", label: "Published Jobs", icon: Briefcase, color: "#E53935" },
  { k: "applications", label: "Applications", icon: FileText, color: "#3B82F6" },
  { k: "candidates", label: "Candidates", icon: Users, color: "#F59E0B" },
  { k: "leads", label: "Hiring Leads", icon: Mailbox, color: "#E53935" },
  { k: "employers", label: "Employers", icon: Building2, color: "#3B82F6" },
  { k: "whatsapp_clicks", label: "WhatsApp Clicks", icon: MessageCircle, color: "#25D366" },
  { k: "blogs", label: "Blog Posts", icon: Newspaper, color: "#F59E0B" },
  { k: "events", label: "Tracked Events", icon: TrendingUp, color: "#E53935" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  if (!stats) return <div className="grid place-items-center h-64"><div className="h-10 w-10 rounded-full border-2 border-brand-emerald border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-display text-2xl font-bold">Dashboard</h1>
        <p className="text-slate-400 text-sm mt-1">Overview of your recruitment platform.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {CARDS.map((c) => (
          <div key={c.k} className="glass rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <span className="grid place-items-center h-10 w-10 rounded-xl" style={{ background: `${c.color}22`, color: c.color }}><c.icon size={20} /></span>
            </div>
            <div className="mt-3 font-display text-3xl font-extrabold">{stats[c.k] ?? 0}</div>
            <div className="text-xs text-slate-400 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="glass rounded-2xl p-5">
        <h3 className="font-display font-semibold mb-4">Event Breakdown</h3>
        {stats.event_breakdown?.length ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.event_breakdown}>
              <XAxis dataKey="event" tick={{ fill: "#94A3B8", fontSize: 11 }} angle={-25} textAnchor="end" height={70} interval={0} />
              <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1E293B", borderRadius: 12 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {stats.event_breakdown.map((_, i) => <Cell key={i} fill={["#E53935", "#3B82F6", "#F59E0B"][i % 3]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : <p className="text-slate-400 text-sm">No events tracked yet.</p>}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="glass rounded-2xl p-5">
          <h3 className="font-display font-semibold mb-3">Recent Applications</h3>
          <div className="space-y-2">
            {stats.recent_applications?.length ? stats.recent_applications.map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                <div><div className="font-medium">{a.name}</div><div className="text-xs text-slate-500">{a.job_title}</div></div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-brand-emerald/15 text-brand-emerald">{a.status}</span>
              </div>
            )) : <p className="text-slate-400 text-sm">No applications yet.</p>}
          </div>
        </div>
        <div className="glass rounded-2xl p-5">
          <h3 className="font-display font-semibold mb-3">Recent Hiring Leads</h3>
          <div className="space-y-2">
            {stats.recent_leads?.length ? stats.recent_leads.map((l) => (
              <div key={l.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                <div><div className="font-medium">{l.company}</div><div className="text-xs text-slate-500">{l.job_role} · {l.location}</div></div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-brand-blue/15 text-brand-blue">{l.crm_stage}</span>
              </div>
            )) : <p className="text-slate-400 text-sm">No leads yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
